import React, { useEffect } from 'react';
import { Table, Popconfirm, message, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConferences, deleteConference } from '../../features/conferenceSlice';
import { useNavigate } from 'react-router-dom';
import App from '../../Utils/LayoutMobile';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const PlusIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const EditIcon = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const TrashIcon = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const CalendarIcon = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const ClockIcon = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const ConferenceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { list, loading } = useSelector((state) => state.conference);

  useEffect(() => {
    dispatch(fetchConferences());
  }, [dispatch]);

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteConference(id)).unwrap();
      message.success('Event deleted successfully');
      dispatch(fetchConferences());
    } catch (err) {
      console.error(err);
      message.error('Failed to delete event');
    }
  };

  const columns = [
    {
      title: 'Event Details',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 800, color: C_TEXT_MAIN, fontSize: "15px", marginBottom: "4px" }}>
            {text}
          </div>
          <div style={{ fontSize: "12px", color: C_PINK, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {record.conferenceType || "Event"}
          </div>
        </div>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MAIN, fontSize: "13.5px", fontWeight: 600 }}>
            <span style={{ color: C_PURPLE }}><CalendarIcon /></span>
            {record.date ? new Date(record.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : "TBA"}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MUTED, fontSize: "12.5px" }}>
            <ClockIcon />
            {record.startTime ? dayjs(record.startTime, 'HH:mm').format('hh:mm A') : "--"} - {record.endTime ? dayjs(record.endTime, 'HH:mm').format('hh:mm A') : "--"}
          </div>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: "center",
      render: (status) => {
        let statusClass = "status-draft";
        if (status === "ongoing") statusClass = "status-ongoing";
        if (status === "upcoming") statusClass = "status-upcoming";
        return (
          <span className={`status-pill ${statusClass}`}>
            <span className="status-dot" />
            {status || "draft"}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <Space size="middle">
          <button className="action-btn edit-btn" onClick={() => navigate(`/conference/edit/${record._id}`)} title="Edit Event">
            <EditIcon />
          </button>
          <Popconfirm
            title={<span style={{ fontWeight: 700, color: C_TEXT_MAIN }}>Delete Event?</span>}
            description="Are you sure you want to permanently delete this event?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes, delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, style: { borderRadius: "8px", fontWeight: 600 } }}
            cancelButtonProps={{ style: { borderRadius: "8px", fontWeight: 600 } }}
          >
            <button className="action-btn delete-btn" title="Delete Event">
              <TrashIcon />
            </button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          {/* Header */}
          <div className="header-flex">
            <div>
              <SectionLabel>Event Management</SectionLabel>
              <h1 className="sec-title">Conference <span style={{ color: C_PINK }}>& Events</span></h1>
              <p className="sec-sub">Manage conference schedules, sessions, and ticket pricing.</p>
            </div>
            <button className="btn-cta" onClick={() => navigate('/conference/create')}>
              <PlusIcon /> Add Event
            </button>
          </div>

          {/* Table */}
          <div className="glass table-wrapper">
            <Table
              columns={columns}
              dataSource={list}
              rowKey="_id"
              loading={loading}
              pagination={{ pageSize: 10, position: ["bottomCenter"] }}
              className="premium-table"
            />
          </div>

        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        .lc { font-family: 'Segoe UI', sans-serif; background: ${C_SOFT_BG}; min-height: 100vh; padding: 40px 0 80px; }
        .mw { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

        .header-flex { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 40px; gap: 20px; flex-wrap: wrap; }
        .sec-title { font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; letter-spacing: -0.02em; line-height: 1.15; margin: 0 0 8px; }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }

        .btn-cta { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: ${C_GRAD}; border: none; border-radius: 50px; color: #fff; font-size: 14.5px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 24px rgba(240,77,160,0.3); transition: all 0.25s; white-space: nowrap; }
        .btn-cta:hover { transform: translateY(-2px); box-shadow: 0 14px 28px rgba(240,77,160,0.4); }

        .status-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 50px; font-size: 12px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
        .status-dot { width: 6px; height: 6px; border-radius: 50%; }
        
        .status-ongoing { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
        .status-ongoing .status-dot { background: #16a34a; }
        
        .status-upcoming { background: #fdf4ff; color: ${C_PURPLE}; border: 1px solid #f5d0fe; }
        .status-upcoming .status-dot { background: ${C_PURPLE}; }

        .status-draft { background: #f8f9fa; color: #64748b; border: 1px solid #e2e8f0; }
        .status-draft .status-dot { background: #94a3b8; }

        .action-btn { width: 38px; height: 38px; border-radius: 10px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; }
        .edit-btn { background: rgba(139,47,201,0.08); color: ${C_PURPLE}; }
        .edit-btn:hover { background: rgba(139,47,201,0.15); transform: translateY(-2px); }
        .delete-btn { background: rgba(248, 113, 113, 0.1); color: #ef4444; }
        .delete-btn:hover { background: rgba(248, 113, 113, 0.2); transform: translateY(-2px); }

        .glass { background: #fff; border-radius: 20px; padding: 12px 24px 24px; border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); overflow: hidden; }

        .premium-table .ant-table { background: transparent !important; font-family: inherit; }
        .premium-table .ant-table-thead > tr > th { background: transparent !important; color: ${C_TEXT_MUTED}; font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid rgba(139,47,201,0.08); padding: 20px 16px; }
        .premium-table .ant-table-thead > tr > th::before { display: none !important; }
        .premium-table .ant-table-tbody > tr > td { border-bottom: 1px solid rgba(139,47,201,0.05); padding: 18px 16px; transition: background 0.3s; }
        .premium-table .ant-table-tbody > tr:hover > td { background: #fcfaff !important; }
        .premium-table .ant-pagination { margin-top: 24px !important; }
        .premium-table .ant-pagination-item-active { border-color: ${C_PINK}; background: rgba(240,77,160,0.05); }
        .premium-table .ant-pagination-item-active a { color: ${C_PINK} !important; }

        @media(max-width: 768px) {
          .glass { padding: 12px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .btn-cta { width: 100%; justify-content: center; }
        }
      `}</style>
    </App>
  );
};

export default ConferenceList;